import { RefinementValue } from 'react-instantsearch-core'
import Helper from 'utils/Helper.class'
import { isBefore, parse } from 'date-fns'

export type Facet = {
  widget: any
  name: string
  allowedEntities: EntityTypes[]
  facet: string
  label?: string
  transformItems?: (items: any) => string
  transformValue?: (value: number | string) => string
}

export type Facets = {
  index: string
  facets: Facet[]
}

export type RefineListItem = {
  value: RefinementValue
  label: string
  refineValues?: RefinementValue[]
}

export type Refine = {
  count: number
  isRefined: boolean
  label: string
  value: string[]
}

export enum EntityTypes {
  Default = 'DEFAULT',
  Member = 'MEMBER',
}

const ANYTIME = 'Anytime'
const DATE_FORMAT = 'MMMM yyyy'

const transformDateItems = (items: Refine[]): any => {
  const res = items.reduce((acc: { [key: string]: any }, item: Refine) => {
    let data = { ...acc }
    const itemValues = item.value.length ? item.value : [item.label]
    itemValues.forEach((value: string) => {
      const isAllItem = parseInt(value, 10) === 0
      const dateName = isAllItem
        ? ANYTIME
        : Helper.getMonthAndYear(Number(value))
      const exitedData = data[dateName]
      data = {
        ...data,
        [dateName]: {
          isRefined: exitedData?.isRefined || item.isRefined,
          label: dateName,
          value: isAllItem ? ['0'] : [...(exitedData?.value || []), value],
        },
      }
    })
    return data
  }, {})
  return Object.values(res).sort((a: RefineListItem, b: RefineListItem) => {
    if (a.label === ANYTIME) return -1
    if (b.label === ANYTIME) return 1
    return isBefore(
      parse(a.label, DATE_FORMAT, new Date()),
      parse(b.label, DATE_FORMAT, new Date()),
    )
      ? -1
      : 1
  })
}

const facets: Facets[] = [
  {
    index: 'master',
    facets: [
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        facet: 'topics',
        allowedEntities: [EntityTypes.Default],
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },
  {
    index: 'courses',
    facets: [
      {
        widget: 'ToggleRefinement',
        name: 'Access',
        label: 'Member Benefit',
        allowedEntities: [EntityTypes.Default],
        facet: 'memberBenefit',
      },
      {
        widget: 'RefinementList',
        name: 'Location',
        facet: 'purchasableItems.location',
        allowedEntities: [EntityTypes.Default],
      },
      {
        widget: 'DateRange',
        name: 'Date',
        facet: 'purchasableItems.beginDate',
        allowedEntities: [EntityTypes.Default],
        transformItems: transformDateItems,
      },
      {
        widget: 'RefinementList',
        name: 'Delivery Method',
        allowedEntities: [EntityTypes.Default],
        facet: 'purchasableItems.deliveryMethods',
      },
      {
        widget: 'RangeSlider',
        name: 'Number of days',
        allowedEntities: [EntityTypes.Default],
        facet: 'purchasableItems.duration',
      },
      {
        widget: 'RangeSlider',
        name: 'List Price',
        allowedEntities: [EntityTypes.Default],
        facet: 'purchasableItems.listPrice',
        transformValue: (value: string | number) => `$${value}`,
      },
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        allowedEntities: [EntityTypes.Default],
        facet: 'topics',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
      {
        widget: 'RefinementList',
        name: 'Language',
        allowedEntities: [EntityTypes.Default],
        facet: 'languages',
      },
      {
        widget: 'RefinementList',
        name: 'Level',
        allowedEntities: [EntityTypes.Default],
        facet: 'levels',
      },
    ],
  },
  {
    index: 'performance_resources',
    facets: [
      {
        widget: 'ToggleRefinement',
        name: 'Access',
        label: 'Member Benefit',
        allowedEntities: [EntityTypes.Default],
        facet: 'memberBenefit',
      },
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        allowedEntities: [EntityTypes.Default],
        facet: 'topics',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },
  {
    index: 'conferences',
    facets: [
      {
        widget: 'RefinementList',
        name: 'Location',
        allowedEntities: [EntityTypes.Default],
        facet: 'purchasableItems.location',
      },
      {
        widget: 'RefinementList',
        name: 'Date',
        allowedEntities: [EntityTypes.Default],
        facet: 'purchasableItems.beginDate',
        transformItems: transformDateItems,
      },
      {
        widget: 'RefinementList',
        name: 'Delivery Method',
        allowedEntities: [EntityTypes.Default],
        facet: 'purchasableItems.deliveryMethods',
      },
      {
        widget: 'RangeSlider',
        name: 'Number of days',
        allowedEntities: [EntityTypes.Default],
        facet: 'purchasableItems.duration',
      },
      {
        widget: 'RangeSlider',
        name: 'List Price',
        allowedEntities: [EntityTypes.Default],
        facet: 'purchasableItems.listPrice',
        transformValue: (value: string | number) => `$${value}`,
      },
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        allowedEntities: [EntityTypes.Default],
        facet: 'topics',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },
  {
    index: 'podcasts',
    facets: [],
  },
  {
    index: 'videos',
    facets: [
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'ToggleRefinement',
        name: 'Access',
        label: 'Member Benefit',
        allowedEntities: [EntityTypes.Default],
        facet: 'memberBenefit',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },
  {
    index: 'webinars',
    facets: [
      {
        widget: 'ToggleRefinement',
        name: 'Access',
        label: 'Member Benefit',
        allowedEntities: [EntityTypes.Default],
        facet: 'memberBenefit',
      },
      {
        widget: 'RangeSlider',
        name: 'Duration (mins)',
        allowedEntities: [EntityTypes.Default],
        facet: 'duration',
      },
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        allowedEntities: [EntityTypes.Default],
        facet: 'topics',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },
  {
    index: 'publications',
    facets: [
      {
        widget: 'ToggleRefinement',
        name: 'Access',
        label: 'Member Benefit',
        allowedEntities: [EntityTypes.Default],
        facet: 'memberBenefit',
      },
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        allowedEntities: [EntityTypes.Default],
        facet: 'topics',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },

  {
    index: 'articles',
    facets: [
      {
        widget: 'ToggleRefinement',
        name: 'Access',
        label: 'Member Benefit',
        allowedEntities: [EntityTypes.Default],
        facet: 'memberBenefit',
      },
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        allowedEntities: [EntityTypes.Default],
        facet: 'topics',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },
  {
    index: 'blogs',
    facets: [
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        allowedEntities: [EntityTypes.Default],
        facet: 'topics',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },
  {
    index: 'pages',
    facets: [
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        allowedEntities: [EntityTypes.Default],
        facet: 'topics',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },
  {
    index: 'certification',
    facets: [
      {
        widget: 'RefinementList',
        name: 'Role',
        allowedEntities: [EntityTypes.Default],
        facet: 'roles',
      },
      {
        widget: 'RefinementList',
        name: 'Topic',
        allowedEntities: [EntityTypes.Default],
        facet: 'topics',
      },
      {
        widget: 'RefinementList',
        name: 'Industry',
        allowedEntities: [EntityTypes.Default],
        facet: 'industries',
      },
    ],
  },
]

export default facets
